import { useMemo, useState } from "react"

import { useMutation } from "@apollo/client"

import { isUnclaimableCancel } from "./bundleClaimValidation"

import { graphql } from "@/main/gql/__generated__"
import { CancelForListFragment } from "@/main/gql/__generated__/graphql"

const BundleClaim = graphql(/* GraphQL */ `
	mutation BundleClaim($input: BundleClaimInput!) {
		bundleClaim(input: $input) {
			failedCancelNames
		}
	}
`)

export const useBundleClaim = (
	checkedCancelMap: Record<number, CancelForListFragment>,
	onBundleClaimingSuccess: (cancelIds: string[]) => void
) => {
	const [mutateBundleClaim] = useMutation(BundleClaim)

	const [isBundleClaimConfirmOpen, setIsBundleClaimConfirmOpen] =
		useState(false)
	const [isBundleClaimSubmitting, setIsBundleClaimSubmitting] = useState(false)
	const [bundleClaimFailedCancels, setBundleClaimFailedCancels] = useState<
		CancelForListFragment[]
	>([])
	const [isBundleClaimErrorOpen, setIsBundleClaimErrorOpen] = useState(false)

	const selectedCancels = useMemo(() => {
		return Object.values(checkedCancelMap)
	}, [checkedCancelMap])

	const onClickBundleClaimButton = () => {
		if (selectedCancels.length === 0) return

		const unclaimable = selectedCancels.filter(isUnclaimableCancel)
		if (unclaimable.length > 0) {
			setBundleClaimFailedCancels(unclaimable)
			setIsBundleClaimErrorOpen(true)
			return
		}

		setIsBundleClaimConfirmOpen(true)
	}

	const onConfirmBundleClaim = () => {
		setIsBundleClaimSubmitting(true)
		const cancelIds = selectedCancels.map((cancel) => cancel.idv2)
		mutateBundleClaim({
			variables: {
				input: { cancelIds },
			},
		})
			.then((result) => {
				setIsBundleClaimConfirmOpen(false)

				const failedNames = result.data?.bundleClaim.failedCancelNames ?? []
				if (failedNames.length > 0) {
					alert(`以下の請求に失敗しました:\n${failedNames.join("\n")}`)
				} else {
					alert("請求を送信しました。")
				}

				onBundleClaimingSuccess(cancelIds)
			})
			.catch((error) => {
				console.error("BundleClaim mutation failed:", error)
				setIsBundleClaimConfirmOpen(false)
				alert("請求の送信中にエラーが発生しました。")
			})
			.finally(() => {
				setIsBundleClaimSubmitting(false)
			})
	}

	return {
		selectedCancelsForBundleClaim: selectedCancels,
		isBundleClaimConfirmOpen,
		isBundleClaimSubmitting,
		isBundleClaimErrorOpen,
		bundleClaimFailedCancels,
		onClickBundleClaimButton,
		onConfirmBundleClaim,
		closeBundleClaimConfirm: () => setIsBundleClaimConfirmOpen(false),
		closeBundleClaimError: () => setIsBundleClaimErrorOpen(false),
	}
}
